import {Injectable, OnDestroy} from '@angular/core';
import {IImmoconnData} from '../interfaces/immoconnData.interface';
import {
  IDataGWSampleResponse,
  IDataGWSampleResultResponse,
  IImmoConnDataGPIOItem,
  IImmoConnDataOneWireItem,
} from '../interfaces/iotMaxxGateway.response';
import {IDeviceData, IUvsModule} from '../interfaces/juconnGateway.response';
import {MeterTypeEnum} from '../models/meter-type.enum';
import {ShareService} from "./share.service";
import {DeviceDataService} from "./device-data.service";
import {combineLatest, Subscription} from "rxjs";
import {OtherSensorModel} from "../models/other-sensor.model";
import {OtherSensorOptionEnum} from '../enums/other-sensor-option-enum';
import {IRMeterTypeEnum} from "../models/ir-meter-type.enum";

@Injectable({
  providedIn: "root",
})
export class ConverterService implements OnDestroy {
  private sub: Subscription;

  constructor(
    private shareService: ShareService,
    private deviceDataService: DeviceDataService
  ) {
    this.sub = combineLatest([
      this.deviceDataService.deviceData$,
      this.deviceDataService.moduleConfig$
    ]).subscribe(([data]) => {
      if (data?.deviceData) {
        this.updateCollectedDataWithValues(data.deviceData);
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  public updateCollectedDataWithValues(
    data: IDeviceData
  ) {
    console.log("updateCollectedDataWithValues - dataResponse", data);

    const sensorsTemperature = this.shareService.sensorsTemperature;
    const sensorsPump = this.shareService.sensorsPump;
    const sensorsCounter = this.shareService.sensorsCounter;
    const sensorsOther = this.shareService.sensorsOther;

    if (sensorsTemperature?.temperatureSensorsHelperList?.length > 0) {
      sensorsTemperature.temperatureSensorsHelperList.forEach((temperatureSensor) => {
        temperatureSensor.value = this.resolveTemperatureValue(data, temperatureSensor.sensorId);
        temperatureSensor.value2 = this.resolveTemperatureValue(data, temperatureSensor.sensor2Id);
        temperatureSensor.value3 = this.resolveTemperatureValue(data, temperatureSensor.sensor3Id);
      });
      this.shareService.sensorsTemperature = sensorsTemperature;
    }

    if (sensorsPump?.pumpSensors?.length > 0) {
      sensorsPump.pumpSensors.forEach((pumpSensor) => {
        if (!pumpSensor?.sensorId) {
          return;
        }
        const [modulePart, pumpPart] = pumpSensor.sensorId.split('.');
        const moduleNumber = Number(modulePart);
        const pumpNumber = Number(pumpPart);
        const pumpRaw = data?.uvsModule?.[moduleNumber - 1]?.currentLoop?.[pumpNumber - 1];
        const mappedValue = this.getPumpMappedFromRaw(pumpRaw, 0, pumpSensor.sensorType);
        pumpSensor.value = Number.isFinite(mappedValue) ? mappedValue : null;
      });
      this.shareService.sensorsPump = sensorsPump;
    }

    if (sensorsCounter?.counterSensors?.length > 0) {
      sensorsCounter.counterSensors.forEach((meterSensor) => {
        const module = data?.uvsModule?.find(m => m.moduleInfo.address === meterSensor.module);

        if (meterSensor.meterType === MeterTypeEnum.IR && module?.irUart) {
          if (meterSensor.irType === IRMeterTypeEnum.SML || meterSensor.irType === IRMeterTypeEnum.ALE3D5F || meterSensor.irType.toLowerCase() === IRMeterTypeEnum.SML) {
            const meterData = module.irUart.find(uart => uart.input === meterSensor.sensorId && uart.type === 'SML');
            if (meterData) {
              meterSensor.seriesNumber = (meterData.data as any).serverId;
              meterSensor.manufacturer = (meterData.data as any).serverId?.substring(1, 4);
              meterSensor.smlValue = [(meterData.data as any)["1-0:1.8.0*255"], (meterData.data as any)["1-0:2.8.0*255"], (meterData.data as any)["1-0:3.8.0*255"]].filter(value => value !== undefined && value !== null);
              meterSensor.value = Number(meterSensor.smlValue[0]);
            }
          } else {
            const meterData = module.irUart.find(uart => uart.input === meterSensor.sensorId && uart.type === 'METER');
            if (meterData) {
              meterSensor.manufacturer = (meterData.data as any).manufacturer;
              meterSensor.seriesNumber = (meterData.data as any).serial;
              meterSensor.value = (meterData.data as any).Wh;
            }
          }
        }

        if (meterSensor.meterType === MeterTypeEnum.Impulse && module?.impulse?.length) {
          const value = module.impulse[meterSensor.sensorId - 1];
          if (value) {
            meterSensor.value = value;
          }
        }
      });
      this.shareService.sensorsCounter = sensorsCounter;
    }

    if (sensorsOther?.pressureSensors?.length > 0) {
      sensorsOther.pressureSensors.forEach((pressureSensor) => {
        if (!pressureSensor?.sensorId) {
          return;
        }

        const [modulePart, pressurePart] = pressureSensor.sensorId.split('.');
        const moduleNumber = Number(modulePart);
        const pressureNumber = Number(pressurePart);
        const rawPressure = data?.uvsModule?.[moduleNumber - 1]?.currentLoop?.[pressureNumber - 1];

        const minPressure = pressureSensor.lowerLimit;
        const maxPressure = pressureSensor.upperLimit;
        if (Number.isFinite(minPressure) && Number.isFinite(maxPressure) && Number.isFinite(rawPressure)) {
          pressureSensor.value = this.getFourToTwentyMappedFromRaw(
            rawPressure * Math.sqrt(2),
            minPressure,
            maxPressure
          );
        } else {
          pressureSensor.value = null;
        }
      });
    }

    if (data?.uvsModule?.length > 0) {
      data.uvsModule.forEach(module => {
        this.convertToLeakage(module, sensorsOther?.otherSensors, OtherSensorOptionEnum.LEAKAGE_1);
        this.convertToLeakage(module, sensorsOther?.otherSensors, OtherSensorOptionEnum.LEAKAGE_2);

        if (module?.light?.length > 0 && sensorsOther?.otherSensors?.length > 0) {
          const rawLight = module.light[0];
          const sensorIndex = sensorsOther.otherSensors.findIndex(sensor =>
            sensor.uvsModuleId === module.moduleInfo.address &&
            sensor.sensorType === OtherSensorOptionEnum.LIGHT
          );
          const light = this.getLightFromGPIO(rawLight, 450);
          if (sensorIndex > -1) {
            sensorsOther.otherSensors[sensorIndex].sensorOn = light;
          }
        }

        this.convertToShaftContact(module, sensorsOther?.otherSensors, OtherSensorOptionEnum.SHAFT_CONTACT_1);
        this.convertToShaftContact(module, sensorsOther?.otherSensors, OtherSensorOptionEnum.SHAFT_CONTACT_2);
      });
    }

    if (sensorsOther) {
      this.shareService.sensorsOther = sensorsOther;
    }
  }

  private resolveTemperatureValue(data: IDeviceData, sensorId?: string): number | null {
    if (!sensorId) {
      return null;
    }

    const [modulePart, oneWirePart] = sensorId.split('.');
    const moduleNumber = Number(modulePart);
    const oneWireId = oneWirePart;

    if (!moduleNumber || !oneWireId) {
      return null;
    }

    const w1 = data?.uvsModule?.[moduleNumber - 1]?.w1;
    if (!w1?.length) {
      return null;
    }

    const value = this.getOneWireTemperatureByIoTId(w1, oneWireId);
    return Number.isFinite(value) ? value : null;
  }

  public convertGatewayDataToImmoconnData(
    dataResponse
    :
    IDataGWSampleResponse,
    minPressureText ?: string,
    maxPressureText ?: string,
    shaft1Open ?: boolean,
    shaft2Open ?: boolean,
    pump1Type ?: number,
    pump2Type ?: number,
    pump3Type ?: number
  ):
    IImmoconnData | null {
    let tempMinPressureText = minPressureText;
    let tempMaxPressureText = maxPressureText;
    let tempShaft1Open = shaft1Open;
    let tempShaft2Open = shaft2Open;
    let tempPump1Type = pump1Type;
    let tempPump2Type = pump2Type;
    let tempPump3Type = pump3Type;
    let flowTemperatur1;
    let returnTemperatur1;
    let flowTemperatur2;
    let returnTemperatur2;
    let warmWaterTemperatur1;
    let warmWaterTemperatur2;
    let pressure;
    let outTemp;
    let leakage;
    let light;
    let shaft1;
    let shaft2;
    let pump1;
    let pump2;
    let pump3;

    const result: IDataGWSampleResultResponse = dataResponse.result;

    // let parmasOrResult: any;
    // if (dataResponse.params) {
    //   parmasOrResult = dataResponse.params;
    // } else if (dataResponse.result) {
    //   parmasOrResult = dataResponse.result;
    // }
    if (
      result &&
      result.GW4x00 &&
      result.GW4x00.w1 &&
      result.GW4x00.w1.length > 0
    ) {
      flowTemperatur1 = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "10" // id
      );
      returnTemperatur1 = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "11"
      );
      flowTemperatur2 = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "15"
      );
      returnTemperatur2 = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "16"
      );
      warmWaterTemperatur1 = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "12"
      );
      warmWaterTemperatur2 = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "14"
      );
      outTemp = this.getOneWireTemperatureByIoTId(
        result.GW4x00.w1,
        "13"
      );
    }

    if (
      tempMinPressureText &&
      tempMaxPressureText &&
      result &&
      result.GW4x01 &&
      result.GW4x01.CurrentLoop &&
      result.GW4x01.CurrentLoop.length > 0
    ) {
      const rawPressure = result.GW4x01.CurrentLoop[0];
      const minPressureFromCache = +tempMinPressureText;
      const maxPressureFromCache = +tempMaxPressureText;
      pressure = this.getFourToTwentyMappedFromRaw(
        rawPressure,
        minPressureFromCache,
        maxPressureFromCache
      );
    }

    if (result && result.GW4x00 && result.GW4x00.GPIO) {
      const leakageArray = result.GW4x00.GPIO;
      const rawLeckage = leakageArray
        ? result.GW4x00.GPIO[0]
        : undefined;
      if (rawLeckage !== undefined) {
        leakage = this.getLeakageFromGPIO(rawLeckage);
      }
    }

    if (
      result &&
      result.GW4x00 &&
      result.GW4x00.Input &&
      result.GW4x00.Input.length > 0
    ) {
      light = result.GW4x00.Input[1];
    }

    if (
      result &&
      result.GW4x00 &&
      result.GW4x00.Input &&
      result.GW4x00.Input.length > 0
    ) {
      const shaft1OpenFromCache = tempShaft1Open;
      const rawShaft1 = result.GW4x00.Input[2];
      let shaft1Value;
      if (shaft1OpenFromCache === rawShaft1) {
        shaft1Value = true;
      } else {
        shaft1Value = false;
      }
      shaft1 = shaft1Value;

      const shaft2OpenFromCache = tempShaft2Open;
      const rawShaft2 = result.GW4x00.Input[3];
      let shaft2Value;
      if (shaft2OpenFromCache === rawShaft2) {
        shaft2Value = true;
      } else {
        shaft2Value = false;
      }
      shaft2 = shaft2Value;
    }
    if (
      result &&
      result.GW4x01 &&
      result.GW4x01.CurrentLoop &&
      result.GW4x01.CurrentLoop.length > 0
    ) {
      if (tempPump1Type) {
        const pump1Raw = result.GW4x01.CurrentLoop[1]; /// is index so 2 means 1

        pump1 = this.getPumpMappedFromRaw(pump1Raw, 0, tempPump1Type);
        /// pump1 = this.getFourToTwentyMappedFromRaw(pump1Raw data from top, 0, tempPump1Type - sensorart 10/20);
      }

      if (tempPump2Type) {
        const pump2Raw = result.GW4x01.CurrentLoop[2];
        pump2 = this.getPumpMappedFromRaw(pump2Raw, 0, tempPump2Type);
      }

      if (tempPump3Type) {
        const pump3Raw = result.GW4x01.CurrentLoop[3];
        pump3 = this.getPumpMappedFromRaw(pump3Raw, 0, tempPump3Type);
      }
    }

    return {
      flowTemperatur1: flowTemperatur1,
      returnTemperatur1: returnTemperatur1,
      flowTemperatur2: flowTemperatur2,
      returnTemperatur2: returnTemperatur2,
      warmWaterTemperatur1: warmWaterTemperatur1,
      warmWaterTemperatur2: warmWaterTemperatur2,
      outTemp: outTemp,
      leakage: leakage,
      light: light,
      pressure: pressure,
      shaft1: shaft1,
      shaft2: shaft2,
      pump1: pump1,
      pump2: pump2,
      pump3: pump3,
    };
  }

  private getPumpMappedFromRaw(
    raw: any,
    min: number,
    max: number
  ): number | undefined {
    if (max > 1000) {
      return this.getZeroToXMappedFromRaw(raw, 20, max - 1000);
    } else {
      return this.getFourToTwentyMappedFromRaw(raw, min, max)
    }
  }

  private getFourToTwentyMappedFromRaw(
    raw: any,
    min: number,
    max: number
  ):
    number | undefined {
    //since it is a 4-20 mA Sensor
    const asNumber = raw as number;
    if (asNumber) {
      const result = min + (asNumber - 4) * ((max - min) / 16);
      return +result.toFixed(2);
    }

    return;
  }

  private getZeroToXMappedFromRaw(
    raw: any,
    maxMAmp: number,
    maxAmp: number
  ): number {
    const asNumber = raw as number;
    if (asNumber !== undefined && asNumber !== null) {
      const result = (maxAmp - 0) / (maxMAmp - 0) * asNumber;
      console.log(result)
      return +result.toFixed(2);
    }

    return -999;
  }

  private getLeakageFromGPIO(gpio: IImmoConnDataGPIOItem):
    boolean | undefined {
    if (gpio.Output === "pullup") {
      if (gpio.Input) {
        if (gpio.ADC < 5.5) {
          return true;
        } else {
          return false;
        }
      }
    }
    return;
  }

  private getLightFromGPIO(
    gpio: number,
    threshold: number
  ):
    boolean {
    return gpio < threshold;
  }

  private getOneWireTemperatureByIoTId(
    w1: IImmoConnDataOneWireItem[],
    iotId: string
  ):
    number | undefined {
    for (const oneWireSensor of w1) {
      if (oneWireSensor.type === "IOT_ID" && oneWireSensor.name === iotId) {
        if (oneWireSensor.temperature) {
          const interpretedValue = oneWireSensor.temperature.value;
          return interpretedValue;
        }
      }
    }
    return undefined;
  }

  private convertToLeakage(
    module: IUvsModule,
    otherSensors: OtherSensorModel[],
    leakageType: OtherSensorOptionEnum
  ) {
    let leakageIndex = null;
    if (leakageType === OtherSensorOptionEnum.LEAKAGE_1) {
      leakageIndex = 0;
    } else if (leakageType === OtherSensorOptionEnum.LEAKAGE_2) {
      leakageIndex = 1;
    } else {
      return;
    }
    if (module?.leakage?.length > leakageIndex && otherSensors?.length > 0) {
      const sensorId = otherSensors.findIndex(sensor =>
        sensor.uvsModuleId === module.moduleInfo.address &&
        sensor.sensorType === leakageType);
      if (sensorId > -1) {
        otherSensors[sensorId].sensorOn = module.leakage[leakageIndex] < 500;
      }
    }
  }

  private convertToShaftContact(
    module: IUvsModule,
    otherSensors: OtherSensorModel[],
    schaftType: OtherSensorOptionEnum
  ) {
    let shaftIndex = null;
    if (schaftType === OtherSensorOptionEnum.SHAFT_CONTACT_1) {
      shaftIndex = 0;
    } else if (schaftType === OtherSensorOptionEnum.SHAFT_CONTACT_2) {
      shaftIndex = 1;
    } else {
      return;
    }

    if (module?.pfc?.length > shaftIndex) {
      const pfcValue = !!module.pfc[shaftIndex];
      if (otherSensors?.length > 0) {
        const sensorIndex = otherSensors.findIndex(sensor =>
          sensor.uvsModuleId === module.moduleInfo.address &&
          sensor.sensorType === schaftType);
        if (sensorIndex > -1) {
          otherSensors[sensorIndex].sensorOn = pfcValue;
        }
      }
    }
  }
}
